import React, { useState, useEffect, useMemo } from "react";
import { ExternalLink, Search, X, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const PolicyActsPublic = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [activeSubCategoryId, setActiveSubCategoryId] = useState(null);

  // Filter state
  const [filterYear, setFilterYear] = useState('');
  const [filterCircular, setFilterCircular] = useState('');
  const [filterKeyword, setFilterKeyword] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/policy_acts/get_public_content`);
      const result = await res.json();
      if (result.status === 'success' && result.data) {
        setSections(result.data.sections || []);
        setCategories(result.data.categories || []);
        setDocuments(result.data.documents || []);
      }
    } catch (e) {
      console.error('Error fetching policy_acts:', e);
    } finally {
      setLoading(false);
    }
  };

  // Set defaults when data loads
  useEffect(() => {
    if (sections.length && activeSectionId === null) {
      setActiveSectionId(sections[0].id);
    }
  }, [sections]);

  useEffect(() => {
    if (!activeSectionId) return;
    const topCats = categories.filter(c => c.section_id === activeSectionId && c.parent_id == null);
    setActiveCategoryId(topCats[0]?.id || null);
    setActiveSubCategoryId(null);
    clearFilters();
  }, [activeSectionId]);

  useEffect(() => {
    if (!activeCategoryId) {
      setActiveSubCategoryId(null);
      return;
    }
    const subs = categories.filter(c => c.parent_id === activeCategoryId);
    setActiveSubCategoryId(subs.length > 0 ? subs[0].id : null);
    clearFilters();
  }, [activeCategoryId]);

  const activeSection = useMemo(() => sections.find(s => s.id === activeSectionId), [sections, activeSectionId]);
  const topCategories = useMemo(
    () => categories.filter(c => c.section_id === activeSectionId && c.parent_id == null),
    [categories, activeSectionId]
  );
  const subCategories = useMemo(
    () => categories.filter(c => c.parent_id === activeCategoryId),
    [categories, activeCategoryId]
  );

  // Determine which categoryId to filter documents by (sub if exists, else top)
  const targetCategoryId = activeSubCategoryId || activeCategoryId;

  // Filtered + sorted documents
  const filteredDocs = useMemo(() => {
    let data = documents.filter(d => d.category_id === targetCategoryId);
    if (filterYear) data = data.filter(d => d.year === filterYear);
    if (filterCircular.trim()) {
      const q = filterCircular.trim().toLowerCase();
      data = data.filter(d => (d.circular_no || '').toLowerCase().includes(q));
    }
    if (filterKeyword.trim()) {
      const q = filterKeyword.trim().toLowerCase();
      data = data.filter(d =>
        (d.subject_eng || '').toLowerCase().includes(q) ||
        (d.subject_tamil || '').toLowerCase().includes(q) ||
        (d.title_eng || '').toLowerCase().includes(q) ||
        (d.title_tamil || '').toLowerCase().includes(q)
      );
    }
    if (sortConfig.key) {
      data = [...data].sort((a, b) => {
        const aVal = (a[sortConfig.key] ?? '') + '';
        const bVal = (b[sortConfig.key] ?? '') + '';
        const cmp = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return data;
  }, [documents, targetCategoryId, filterYear, filterCircular, filterKeyword, sortConfig]);

  const yearOptions = useMemo(() => {
    const years = [...new Set(
      documents.filter(d => d.category_id === targetCategoryId).map(d => d.year).filter(Boolean)
    )].sort((a, b) => b.localeCompare(a));
    return years;
  }, [documents, targetCategoryId]);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const clearFilters = () => {
    setFilterYear('');
    setFilterCircular('');
    setFilterKeyword('');
    setSortConfig({ key: null, direction: 'asc' });
  };

  const hasActiveFilters = filterYear || filterCircular.trim() || filterKeyword.trim();

  const t = useMemo(() => ({
    sno: language === 'en' ? 'S.No' : 'வ.எண்',
    year: language === 'en' ? 'Year' : 'ஆண்டு',
    circular: language === 'en' ? 'Circular / GO No' : 'சுற்றறிக்கை / அ.ஆ எண்',
    subject: language === 'en' ? 'Subject' : 'பொருள்',
    document: language === 'en' ? 'Document' : 'ஆவணம்',
    yearPlaceholder: language === 'en' ? 'All Years' : 'அனைத்து ஆண்டுகள்',
    circularPlaceholder: language === 'en' ? 'Search Circular / GO No…' : 'சுற்றறிக்கை / அ.ஆ எண் தேடுக…',
    keywordPlaceholder: language === 'en' ? 'Search by keyword…' : 'முக்கிய வார்த்தை தேடுக…',
    clearAll: language === 'en' ? 'Clear Filters' : 'வடிகட்டிகளை அழி',
    noResults: language === 'en' ? 'No records found.' : 'பதிவுகள் இல்லை.',
    found: language === 'en' ? 'record(s) found' : 'பதிவு(கள்) கிடைத்தது',
  }), [language]);

  const pickName = (item) => language === 'en' ? (item?.name_eng || '') : (item?.name_tamil || item?.name_eng || '');
  const pickTitle = (item) => language === 'en' ? (item?.title_eng || '') : (item?.title_tamil || item?.title_eng || '');
  const pickSubject = (item) => language === 'en' ? (item?.subject_eng || '') : (item?.subject_tamil || item?.subject_eng || '');

  const MEDIA_BASE_URL = 'https://kooturavu.tn.gov.in';
  const resolvePdfUrl = (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;           // already absolute
    if (path.startsWith('/')) return path;                  // root-relative (legacy static)
    return `${MEDIA_BASE_URL}/${path}`;                     // backend upload (uploads/xyz.pdf)
  };

  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5" />
      : <ArrowDown className="w-3.5 h-3.5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">No content available</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-10">

      {/* Section Tabs */}
      <div className="bg-white border-b border-gray-300 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSectionId(s.id)}
                className={`px-6 py-4 font-semibold transition-all duration-200 border-b-4 whitespace-nowrap ${
                  activeSectionId === s.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {pickName(s)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Category Tabs */}
        {topCategories.length > 1 && (
          <div className="flex gap-4 mb-6 border-b border-gray-200 overflow-x-auto">
            {topCategories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCategoryId(c.id)}
                className={`px-6 py-3 font-semibold transition-all duration-200 border-b-2 whitespace-nowrap ${
                  activeCategoryId === c.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {pickName(c)}
              </button>
            ))}
          </div>
        )}

        {/* Sub-Category Tabs */}
        {subCategories.length > 0 && (
          <div className="flex gap-2 mb-6 flex-wrap">
            {subCategories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveSubCategoryId(c.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeSubCategoryId === c.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {pickName(c)}
              </button>
            ))}
          </div>
        )}

        {/* Filter Bar */}
        {activeSection && (activeSection.show_year_filter || activeSection.show_circular_filter || activeSection.show_keyword_filter) && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5 shadow-sm">
            <div className="flex flex-wrap gap-3 items-end">

              {!!activeSection.show_year_filter && (
                <div className="flex flex-col gap-1 min-w-[130px]">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.year}</label>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">{t.yearPlaceholder}</option>
                    {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              )}

              {!!activeSection.show_circular_filter && (
                <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.circular}</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text" value={filterCircular}
                      onChange={(e) => setFilterCircular(e.target.value)}
                      placeholder={t.circularPlaceholder}
                      className="w-full border border-gray-300 rounded-md pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {filterCircular && (
                      <button onClick={() => setFilterCircular('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!!activeSection.show_keyword_filter && (
                <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.subject}</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text" value={filterKeyword}
                      onChange={(e) => setFilterKeyword(e.target.value)}
                      placeholder={t.keywordPlaceholder}
                      className="w-full border border-gray-300 rounded-md pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {filterKeyword && (
                      <button onClick={() => setFilterKeyword('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors self-end"
                >
                  <X className="w-4 h-4" />
                  {t.clearAll}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {filteredDocs.length} {t.found}
            </p>
          </div>
        )}

        {/* Document Table */}
        <div className="bg-white shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-center font-semibold text-sm border-r border-blue-500 w-16">
                    {t.sno}
                  </th>
                  {!!activeSection?.show_year_column && (
                    <th
                      className="px-4 py-3 text-left font-semibold text-sm border-r border-blue-500 w-24 cursor-pointer select-none"
                      onClick={() => handleSort('year')}
                    >
                      <span className="flex items-center gap-1.5">
                        {t.year} <SortIcon col="year" />
                      </span>
                    </th>
                  )}
                  {!!activeSection?.show_circular_column && (
                    <th
                      className="px-4 py-3 text-left font-semibold text-sm border-r border-blue-500 w-64 cursor-pointer select-none"
                      onClick={() => handleSort('circular_no')}
                    >
                      <span className="flex items-center gap-1.5">
                        {t.circular} <SortIcon col="circular_no" />
                      </span>
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-semibold text-sm">
                    {activeSection?.show_subject_column ? t.subject : t.document}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">
                      {t.noResults}
                    </td>
                  </tr>
                ) : (
                  filteredDocs.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition-colors border-b border-gray-200`}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium text-center border-r border-gray-200">
                        {index + 1}
                      </td>
                      {!!activeSection?.show_year_column && (
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium border-r border-gray-200">
                          {item.year}
                        </td>
                      )}
                      {!!activeSection?.show_circular_column && (
                        <td className="px-4 py-3 text-sm border-r border-gray-200">
                          {item.pdf_path ? (
                            <a
                              href={resolvePdfUrl(item.pdf_path)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium flex items-center gap-2"
                            >
                              {item.circular_no}
                              <ExternalLink className="w-4 h-4 shrink-0" />
                            </a>
                          ) : item.circular_no}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {(() => {
                          const text = activeSection?.show_subject_column
                            ? (pickSubject(item) || pickTitle(item))
                            : pickTitle(item);
                          return item.pdf_path ? (
                            <a
                              href={resolvePdfUrl(item.pdf_path)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium flex items-start gap-2"
                            >
                              <span>{text}</span>
                              <ExternalLink className="w-4 h-4 shrink-0 mt-0.5" />
                            </a>
                          ) : text;
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyActsPublic;
